/* Native trial checkout. One add and one redirect per page-level attempt. */
(function () {
  'use strict';
  if (window.ZoocaTrialCheckout) return;

  const SELECTOR = 'form[data-zooca-trial-checkout]';
  let phase = 'idle';
  function controls(busy) {
    document.querySelectorAll(SELECTOR).forEach(form => {
      form.setAttribute('aria-busy', String(busy));
      form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = busy; });
    });
  }
  function message(text) {
    const errors = document.querySelectorAll(SELECTOR + ' [data-trial-error]');
    errors.forEach(node => { node.textContent = text; });
    if (text && errors.length === 0) window.alert?.(text);
  }
  const VARIANT = 49534438211910;
  const PLAN = 689292706118;
  const trialItems = cart => cart.items.filter(item => Number(item.variant_id) === VARIANT);
  const correctPlan = item => Number(item.selling_plan_allocation?.selling_plan?.id) === PLAN;
  async function request(path, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await window.fetch((window.Shopify?.routes?.root || '/') + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  }
  async function normalizeTrial(allowAdd) {
    let cart = await request('cart.js');
    let trials = trialItems(cart);
    if (!trials.length) {
      if (!allowAdd) return;
      await request('cart/add.js', { items: [{ id: VARIANT, quantity: 1, selling_plan: PLAN }] });
    } else {
      // Keys distinguish split lines with different properties or selling plans.
      const keep = trials.find(correctPlan) || trials[0];
      if (trials.length > 1 || keep.quantity !== 1) {
        const updates = {};
        trials.forEach(item => { updates[item.key] = item.key === keep.key ? 1 : 0; });
        await request('cart/update.js', { updates });
      }
      // Quantity changes can change line positions; never reuse a stale line index.
      cart = await request('cart.js');
      trials = trialItems(cart);
      if (trials.length !== 1) throw new Error('Cart changed during normalization');
      if (allowAdd && !correctPlan(trials[0])) {
        await request('cart/change.js', {
          line: cart.items.indexOf(trials[0]) + 1, quantity: 1, selling_plan: PLAN
        });
      }
    }
    // Verify the actual cart before allowing checkout; a failed read is not success.
    const verified = trialItems(await request('cart.js'));
    if (verified.length !== 1 || verified[0].quantity !== 1 || (allowAdd && !correctPlan(verified[0]))) {
      throw new Error('Trial cart invariant not satisfied');
    }
  }
  async function normalizeSportTrial() {
    const cart = await request('cart.js');
    const trials = cart.items.filter(item => item.properties?._zooca_sport_trial === '1');
    if (!trials.length) return;
    const keep = trials[0];
    if (trials.length > 1 || keep.quantity !== 1) {
      const updates = {};
      trials.forEach(item => { updates[item.key] = item.key === keep.key ? 1 : 0; });
      await request('cart/update.js', { updates });
    }
    const verified = (await request('cart.js')).items.filter(item => item.properties?._zooca_sport_trial === '1');
    if (verified.length !== 1 || verified[0].quantity !== 1) throw new Error('Sport trial cart invariant not satisfied');
  }
  async function checkout(allowAdd) {
    if (phase !== 'idle') return;
    phase = 'checking';
    controls(true); message('');
    try {
      await normalizeSportTrial();
      await normalizeTrial(allowAdd);
      phase = 'attributing';
      // Independent bound protects checkout even if the attribution API is replaced or hangs.
      let attributionTimer;
      try {
        await Promise.race([
          Promise.resolve().then(() => window.ZoocaAttribution?.beforeCheckout()),
          new Promise(resolve => { attributionTimer = setTimeout(resolve, 300); })
        ]);
      } catch (_) { /* Successful add must still reach checkout. */ }
      finally { clearTimeout(attributionTimer); }
      phase = 'redirecting';
      window.location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
    } catch (_) {
      // The server may have accepted an add whose response was lost. Keep the lock:
      // retrying blindly could add the trial twice. A failed add never redirects.
      phase = 'uncertain';
      message('Vi kunne ikke bekræfte tilføjelsen. Kontrollér din kurv, før du prøver igen.');
    }
  }
  // Back-forward cache restores the old JS instance. Recheck the server cart on re-entry.
  window.addEventListener('pageshow', event => {
    if (event.persisted && ['redirecting', 'uncertain'].includes(phase)) { phase = 'idle'; controls(false); }
  });
  function matchesTrial(variant, plan) {
    return Number(variant) === VARIANT && Number(plan) === PLAN;
  }
  function trialLink(link) {
    try {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== new URL(window.location.href).origin || url.pathname !== '/tools/recurring/checkout_link') return false;
      const data = JSON.parse(window.atob(url.searchParams.get('magic')));
      return data.items?.length === 1 && Number(data.items[0].quantity) === 1 && matchesTrial(data.items[0].id, data.items[0].selling_plan);
    } catch (_) { return false; }
  }
  function cartLink(link) {
    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === new URL(window.location.href).origin && /\/checkout\/?$/.test(url.pathname) && !url.search && !url.hash;
    } catch (_) { return false; }
  }
  window.ZoocaTrialCheckout = Object.freeze({
    matchesTrial, handlesLink: link => trialLink(link) || cartLink(link),
    purchase: () => checkout(true), checkoutCart: () => checkout(false)
  });
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || !window.ZoocaTrialCheckout.handlesLink(link)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (!window.Shopify?.designMode) void checkout(trialLink(link));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    const variant = form.elements?.namedItem('id')?.value;
    const plan = form.elements?.namedItem('selling_plan')?.value;
    let isCart = false, isProduct = false;
    try {
      const url = new URL(form.action, window.location.href);
      if (url.origin === new URL(window.location.href).origin) {
        isCart = /\/cart\/?$/.test(url.pathname) && event.submitter?.name === 'checkout';
        isProduct = /\/cart\/add(?:\.js)?\/?$/.test(url.pathname) && matchesTrial(variant, plan);
      }
    } catch (_) {}
    if (!isCart && !isProduct) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (!window.Shopify?.designMode) void checkout(isProduct);
  }, true);
  document.addEventListener('shopify:section:load', () => controls(phase !== 'idle'));
})();
